import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useNotifications } from "@/contexts/NotificationsContext";
import { debugLogger } from '@/utils/debugLogger';
import { ScrollArea } from "@/components/ui/scroll-area";
import { OnboardingGuard } from "@/components/guards/OnboardingGuard";
import { useIsMobile } from "@/hooks/use-mobile";

// Components
import NotificationsHeader from "@/components/notifications/NotificationsHeader";
import { GroupedNotificationsList } from "@/components/notifications/GroupedNotificationsList";
import PullToRefresh from "@/components/notifications/PullToRefresh";

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { 
    notifications, 
    loading, 
    removeNotification, 
    markAsRead,
    markAllAsRead, 
    deleteAllNotifications,
    unreadCount,
    notificationsEnabled,
    setNotificationsEnabled,
    refetch
  } = useNotifications();

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (!user && !loading) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // Fallback para detectar perda de sessão após inatividade
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!user && !loading && !window.location.pathname.includes('/auth')) {
        debugLogger.warn('Notifications', 'Session lost after inactivity, redirecting to auth');
        navigate('/auth', { replace: true });
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [user, loading, navigate]);

  // Always scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleNotificationDismiss = useCallback(async (notificationId: string) => {
    await removeNotification(notificationId);
  }, [removeNotification]);

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    await markAsRead(notificationId);
  }, [markAsRead]);

  const handleToggleNotifications = () => {
    setNotificationsEnabled(!notificationsEnabled);
  };

  const handleRefresh = async () => {
    if (refetch) {
      await refetch();
    }
  };

  // Loading state
  if (loading && notifications.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900">
        <NotificationsHeader 
          unreadCount={0}
          onMarkAllAsRead={markAllAsRead}
          onDeleteAll={deleteAllNotifications}
          notificationsEnabled={notificationsEnabled}
          toggleNotifications={handleToggleNotifications}
        />
        <div className="pt-20 px-4 pb-24 md:pb-8">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-white/5 rounded-lg"></div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-white/70">Carregando avisos...</p>
          </div>
        </div>
      </div>
    );
  }

  const notificationContent = (
    <div className="pt-20 px-4 pb-24 md:pb-8">
      <GroupedNotificationsList
        notifications={notifications}
        onDismiss={handleNotificationDismiss}
        onMarkAsRead={handleMarkAsRead}
        isMobile={isMobile}
      />
    </div>
  );

  return (
    <OnboardingGuard>
      <div className="h-screen bg-gradient-to-br from-black via-black to-gray-900 flex flex-col">
        <NotificationsHeader 
          unreadCount={unreadCount}
          onMarkAllAsRead={markAllAsRead}
          onDeleteAll={deleteAllNotifications}
          notificationsEnabled={notificationsEnabled}
          toggleNotifications={handleToggleNotifications}
        />
        
        {/* Scrollable notifications container with pull-to-refresh on mobile */}
        <div className="flex-1 overflow-hidden">
          {isMobile ? (
            <PullToRefresh onRefresh={handleRefresh}>
              <ScrollArea className="h-full">
                {notificationContent}
              </ScrollArea>
            </PullToRefresh>
          ) : (
            <ScrollArea className="h-full">
              {notificationContent}
            </ScrollArea>
          )}
        </div>

        {/* Mobile swipe hint */}
        {isMobile && notifications.length > 0 && (
          <div className="fixed bottom-20 left-0 right-0 flex justify-center pointer-events-none">
            <div className="bg-black/80 backdrop-blur-sm text-white/60 text-xs px-3 py-1.5 rounded-full animate-fade-in">
              ← Deslize para deletar • Deslize para marcar como lida →
            </div>
          </div>
        )}
      </div>
    </OnboardingGuard>
  );
};

export default Notifications;
